import { filter } from '@execonline-inc/collections';
import { PaginationMetadata } from '@execonline-inc/resource';
import { Maybe as StructuredMaybe } from '@execonline-inc/structured-maybe';
import { AlreadyTranslatedText } from '@execonline-inc/translations';
import { InvalidUrlError } from '@execonline-inc/url';
import { always, assertNever } from '@kofno/piper';
import { Maybe, fromEmpty, just, nothing } from 'maybeasy';
import { AppyError } from '../../Appy';
import { currentUserStore } from '../../CurrentUserStore';
import { MissingLinkError } from '../../LinkyLinky';
import { SearchParameter, SearchParameters } from '../../ListFilter/Types';
import { FormTemplate, HasFormTemplates, findField } from '../../Resource/FormTemplates/Types';
import { Resource, ResourceWithMetadata } from '../../Resource/Types';
import { defaultOrgFilter } from '../Form/OrganizationFilterDynamicTypeaheadSelect/Filters/Organization';
import { StringOption } from '../Options/Types';
import LearningCollectionEditStore from './LearningCollection/Edit/Store';
import {
  CommerceType,
  CommonState as EditCommonState,
  LearningCollectionEditFormPayload,
  LearningCollectionEditFormResource,
} from './LearningCollection/Edit/Store/Types';
import LearningDesignExperienceStore from './LearningCollection/Experience/Store';
import {
  experienceStore,
  experienceStoreWithSelectedIds,
} from './LearningCollection/Experience/Types';
import LearningCollectionStore from './LearningCollection/Store';
import {
  CompetencyFilterOptionData,
  LearningDesignState,
  CommonState as NewCommonState,
  ProductTypeFilterOptionData,
} from './LearningCollection/Types';

export type LearningCollectionsError = AppyError | MissingLinkError | InvalidUrlError;
export type EitherLearningCollectionStore = LearningCollectionStore | LearningCollectionEditStore;

// Explicitly setting this here to exclude 'group-coaching' from the list of options for LC
// without removing the option from AS completely.
const availableOfferingTypesForLearningCollections: OfferingType[] = [
  'aep',
  'epc',
  'coaching',
  'program-sequence',
];

export const isOfferingTypeAvailableForLearningCollections = (offeringType: OfferingType) =>
  availableOfferingTypesForLearningCollections.includes(offeringType);

export const getUniqueOfferingTypes = (offeringTypes: ReadonlyArray<OfferingType>) =>
  offeringTypes.filter((offeringType, i) => offeringTypes.indexOf(offeringType) === i);

export const getOfferingTypesFor = (
  store: EitherLearningCollectionStore,
  state: NewCommonState | EditCommonState,
): ReadonlyArray<OfferingType> => {
  const browseAllExperiences = store.browseAll ? state.browseAllExperiences.experienceStores : [];

  const sectionsExperiences = state.sections.flatMap(
    (s) => s.learningDesignExperiences.experienceStores,
  );

  return [
    ...new Set(
      [...browseAllExperiences, ...sectionsExperiences]
        .map((experienceStore) => experienceStore.experienceResource.payload.offeringType)
        .filter((offeringType) => isOfferingTypeAvailableForLearningCollections(offeringType)),
    ),
  ];
};

export const statusFilterSelectOptions: StringOption[] = [
  {
    value: 'all',
    label: 'All',
  },
  {
    value: 'active',
    label: 'Active',
  },
  {
    value: 'draft',
    label: 'Draft',
  },
];

export const sortFilterSelectOptions: StringOption[] = [
  {
    value: 'name-asc',
    label: 'Name A to Z',
  },
  {
    value: 'name-desc',
    label: 'Name Z to A',
  },
  {
    value: 'updated_at-desc',
    label: 'Last Updated: Newest First',
  },
  {
    value: 'updated_at-asc',
    label: 'Last Updated: Oldest First',
  },
];

export type SortOptionValue = 'name-asc' | 'name-desc' | 'updated_at-asc' | 'updated_at-desc';

export type SortIntention = [sortBy: string, sortDirection: 'asc' | 'desc'];

export interface SortParam {
  key: 'sort_by' | 'sort_direction';
  value: SortIntention[number];
}

export interface SupportedLanguage {
  code: string;
  englishName: string;
  localName: string;
}
export type SupportedLanguageResource = Resource<SupportedLanguage>;
export type Status = 'Licensed' | 'Unlicensed';

export interface ExperienceBase {
  id: number;
  kind: ExperienceType;
  shortName: Maybe<string>;
  descriptor: Maybe<string>;
  title: AlreadyTranslatedText;
  description: AlreadyTranslatedText;
  totalHours: Maybe<number>;
  hoursPerWeek: Maybe<number>;
  schoolPartnerLogoUrl: Maybe<string>;
  schoolPartnerName: string;
  publicDetailedDescriptionHtml: string;
  competencies: AttributeResource[];
  featured: Maybe<Date>;
  availableOnDemandInstanceId: Maybe<number>;
  supportedLanguages: SupportedLanguageResource[];
  programInstanceOptions: ProgramInstanceOptions;
  offeringType: OfferingType;
  status: Status;
}

export type ExperienceType = 'program' | 'coaching' | 'program-sequence' | 'group-coaching';

export interface Attribute {
  id: number;
  name: Maybe<AlreadyTranslatedText>;
  description: Maybe<AlreadyTranslatedText>;
  key: Maybe<string>;
  type: string;
}

export interface SchoolPartner {
  id: number;
  name: AlreadyTranslatedText;
}

export type SchoolPartnerResource = Resource<SchoolPartner>;

export type AttributeResource = Resource<Attribute>;

export interface ProgramInstanceOptionBase {
  id: number;
  title: string;
  startOn: Date;
  endOn: Maybe<Date>;
}

export interface OnDemandProgramInstanceOptionParts {
  kind: 'on-demand';
}

export interface ScheduledProgramInstanceOptionParts {
  kind: 'scheduled';
}

export interface OnDemandProgramInstanceOption
  extends ProgramInstanceOptionBase,
    OnDemandProgramInstanceOptionParts {}

export interface ScheduledProgramInstanceOption
  extends ProgramInstanceOptionBase,
    ScheduledProgramInstanceOptionParts {}

export type ProgramInstanceOption = OnDemandProgramInstanceOption | ScheduledProgramInstanceOption;

export interface ProgramInstanceOptions {
  onDemand: Maybe<OnDemandProgramInstanceOption>;
  scheduled: ScheduledProgramInstanceOption[];
}

export interface Experience extends ExperienceBase {}

export type ExperienceResource = Resource<Experience>;
export type ExperienceResources = ExperienceResource[];
export type ExperiencesResource = Resource<ExperienceResources>;

export type ProductType = 'Core' | 'Add-on';
export type ProductCollectionType = 'learning-design' | 'recommendation-engine';
export type ProductCollectionDisplayType = 'carousel' | 'list' | 'grid';

export interface ProductCollectionBase {
  id: number;
  name: AlreadyTranslatedText;
  shortName: string;
  description: string;
  displayType: ProductCollectionDisplayType;
  displayButtonLabel: Maybe<string>;
  experiencesSchoolPartnerUrls: Array<string>;
  experiences: ExperienceResources;
  learningDesignState: LearningDesignState;
}

export type OfferingType = 'epc' | 'aep' | 'coaching' | 'group-coaching' | 'program-sequence';

export const getFullNameForOfferingType = (offeringType: OfferingType) => {
  switch (offeringType) {
    case 'epc':
      return 'Advanced Leader Programs';
    case 'aep':
      return 'On-Demand Learning';
    case 'coaching':
      return 'Coaching';
    case 'group-coaching':
      return 'Group Coaching';
    case 'program-sequence':
      return 'Development Path';
    default:
      assertNever(offeringType);
  }
};

export interface LearningDesign extends ProductCollectionBase {
  kind: 'learning-design';
  learningDesignState: LearningDesignState;
  sourceLearningDesign: Maybe<LearningDesignResource>;
  totalHours: string;
  totalExperiences: string;
  totalSchoolPartners: string;
  competencies: AttributeResource[];
  schoolPartners: SchoolPartnerResource[];
  offeringTypes: OfferingType[];
  browseAll: boolean;
}

export interface RecommendationEngine extends ProductCollectionBase {
  kind: 'recommendation-engine';
}

export interface DynamicRecommendation extends ProductCollectionBase {
  kind: 'dynamic-recommendation';
}

export interface GroupCoachingCollection extends ProductCollectionBase {
  kind: 'group-coaching';
}

export interface AiRecommendation extends ProductCollectionBase {
  kind: 'ai-recommendation';
}

export type ProductCollection =
  | LearningDesign
  | RecommendationEngine
  | DynamicRecommendation
  | GroupCoachingCollection
  | AiRecommendation;

export const browseAllField = (template: FormTemplate) =>
  findField('browse_all', 'boolean', template.fields);

export const filterProductCollectionBy = <T>(
  kind: ProductCollectionType,
  productCollections: ProductCollectionResources,
): ReadonlyArray<T> =>
  filter(
    (productCollection: ProductCollectionResource) => productCollection.payload.kind === kind,
    productCollections,
  ).map((productCollectionResource) => productCollectionResource as T);

const allExperiencesFrom = (
  learningCollectionEditFormPayload: LearningCollectionEditFormPayload,
): ExperienceResource[] =>
  learningCollectionEditFormPayload.productCollections
    .flatMap((productCollection) => productCollection.payload.experiences)
    .concat(learningCollectionEditFormPayload.experiences);

export const allCoachingExperiencesFrom = (
  learningCollectionEditFormPayload: LearningCollectionEditFormPayload,
): ReadonlyArray<ExperienceResource> =>
  filter(
    (experienceResource: ExperienceResource) => experienceResource.payload.kind === 'coaching',
    allExperiencesFrom(learningCollectionEditFormPayload),
  );

export const isEcommerceEnabled = (commerceType: CommerceType): Maybe<CommerceType> => {
  switch (commerceType) {
    case 'not-available':
      return nothing();
    case 'cc-only':
    case 'cc-or-direct-bill':
      return just(commerceType);
    default:
      return nothing();
  }
};

const isEcommerceDisabled = (
  payload: LearningCollectionEditFormPayload,
): Maybe<LearningCollectionEditFormPayload> => {
  switch (payload.commerceType) {
    case 'not-available':
      return just(payload);
    case 'cc-only':
    case 'cc-or-direct-bill':
    default:
      return nothing();
  }
};

export const hasNoRecommendationEngines = (payload: LearningCollectionEditFormPayload) =>
  fromEmpty(
    filterProductCollectionBy<LearningDesignResource>(
      'recommendation-engine',
      payload.productCollections,
    ),
  )
    .map<Maybe<LearningCollectionEditFormPayload>>(always(nothing()))
    .getOrElse(() => just(payload));

export const hasNoCoachingExperiences = (payload: LearningCollectionEditFormPayload) =>
  fromEmpty(allCoachingExperiencesFrom(payload))
    .map<Maybe<LearningCollectionEditFormPayload>>(always(nothing()))
    .getOrElse(() => just(payload));

export const isNotBYOLearningCollection =
  (learningDesignState: LearningDesignState) => (payload: LearningCollectionEditFormPayload) => {
    switch (learningDesignState) {
      case 'blank':
      case 'curated':
      case 'curated-custom':
      case 'custom':
      case 'empty':
      case 'browse-all':
        return just(payload);
      case 'build-your-own':
        return nothing();
    }
  };

export const editableLearningDesignsFrom = (
  resource: LearningCollectionEditFormResource,
): ReadonlyArray<SectionableResource> =>
  isEcommerceDisabled(resource.payload)
    .andThen((payload) => fromEmpty(payload.productCollections))
    .getOrElseValue([]);

export const learningDesignExperienceStoresFrom = (
  learningDesign: ReadonlyArray<SectionableResource>,
): LearningDesignExperienceStore[] =>
  learningDesign.flatMap((learningDesign) =>
    learningDesign.payload.experiences.map(experienceStore),
  );

export const getSelectedIds = (
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
  experienceResource: ExperienceResource,
) =>
  learningCollectionEditFormResource.payload.selectedScheduledPrograms.filter((id) =>
    experienceResource.payload.programInstanceOptions.scheduled.some((option) => option.id === id),
  );

const experienceStoreWithSelectedIdsFromLearningCollectionEditFormResource =
  (learningCollectionEditFormResource: LearningCollectionEditFormResource) =>
  (experienceResource: ExperienceResource) =>
    experienceStoreWithSelectedIds(
      experienceResource,
      getSelectedIds(learningCollectionEditFormResource, experienceResource),
    );

export const learningDesignExperienceStoresSelectedIds = (
  learningDesign: LearningDesignResource | ProductCollectionResource,
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
): LearningDesignExperienceStore[] =>
  learningDesign.payload.experiences.map(
    experienceStoreWithSelectedIdsFromLearningCollectionEditFormResource(
      learningCollectionEditFormResource,
    ),
  );

export const learningDesignExperienceStoresSelectedIdsFrom = (
  learningDesign: Maybe<LearningDesignResource>,
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
): LearningDesignExperienceStore[] =>
  learningDesign
    .map((ld) => learningDesignExperienceStoresSelectedIds(ld, learningCollectionEditFormResource))
    .getOrElseValue([]);

const sourceOrActiveLearningDesign = (
  learningDesign: LearningDesignResource,
): LearningDesignResource =>
  learningDesign.payload.sourceLearningDesign.getOrElseValue(learningDesign);

export const selectedLearningDesignFrom = (learningDesign: Maybe<LearningDesignResource>) =>
  learningDesign.map(sourceOrActiveLearningDesign);

export const learningDesignStateFrom = (learningDesign: Maybe<LearningDesignResource>) =>
  learningDesign
    .map((learningDesign) => learningDesign.payload.learningDesignState)
    .getOrElseValue('empty');

export const browseAllExperienceStoresFrom = (
  learningCollectionEditFormResource: LearningCollectionEditFormResource,
) =>
  learningCollectionEditFormResource.payload.experiences.length === 0
    ? learningCollectionEditFormResource.payload.allExperiences.map(experienceStore)
    : learningCollectionEditFormResource.payload.experiences.map(
        experienceStoreWithSelectedIdsFromLearningCollectionEditFormResource(
          learningCollectionEditFormResource,
        ),
      );

export interface ShareUrl {
  id: number;
  name: string;
  useCaseId: number;
}

export interface PublicNameProgramFamilyShareUrl {
  name: AlreadyTranslatedText;
  shareUrl: ProgramFamilyShareUrlResource;
  experience: ExperienceResource;
}

export interface ProgramFamilyShareUrl {
  id: number;
  name: Maybe<string>;
  useCaseId: Maybe<number>;
  programFamilyId: Maybe<number>;
  deliveryChannel: string;
  active: boolean;
  user: string;
  createdAt: Date;
  programInstanceOptions: ProgramInstanceOptions;
}
export interface ReadonlyField<T> {
  kind: 'readonly';
  value: T;
}

export interface WritableField<T> {
  kind: 'writable';
  value: T;
}

export type ProtectedField<T> = ReadonlyField<T> | WritableField<T>;

export type ProtectedFieldKind = ProtectedField<{}>['kind'];

export type ProgramFamilyShareUrlResource = Resource<ProgramFamilyShareUrl>;

export type ProductCollectionResource = Resource<ProductCollection> & HasFormTemplates;
export type ProductCollectionResources = ProductCollectionResource[];

export type LearningDesignResource = Resource<LearningDesign> & HasFormTemplates;

export type SectionableResource = LearningDesignResource | ProductCollectionResource;
export interface LearningDesignResourcesPayload {
  timestamp: string;
  learningDesignResources: LearningDesignResource[];
  competencyFilterOptions: CompetencyFilterOptionData[];
  productTypeFilterOptions: ProductTypeFilterOptionData[];
}

export type LearningDesignsResource = ResourceWithMetadata<
  LearningDesignResourcesPayload,
  PaginationMetadata
>;

export interface ProductLicense {
  id: number;
  title: AlreadyTranslatedText;
  licenseQuantity: number;
  licenseTypePublicName: Maybe<AlreadyTranslatedText>;
  licenseTypePublicDescription: Maybe<AlreadyTranslatedText>;
  licenseTypeWhoIsItFor: Maybe<AlreadyTranslatedText>;
  licenseTypeIncludesFollowingContentHeader: Maybe<AlreadyTranslatedText>;
  licenseTypeIncludesFollowingContentDesc: Maybe<AlreadyTranslatedText>;
  licenseTypeCoachingPfLabel: Maybe<AlreadyTranslatedText>;
  licenseTypeAepPfLabel: Maybe<AlreadyTranslatedText>;
  licenseTypeEpcPfLabel: Maybe<AlreadyTranslatedText>;
  daysUntilRenewal: number;
  competencies: AttributeResource[];
  extensionIds: number[];
  activationsRemaining: Maybe<number>;
  productType: ProductType;
  offeringTypes: OfferingType[];
  displayActivationsRemaining: boolean;
  displayRenewalNotice: boolean;
  endOn: Date;
}
export type ProductLicenseResource = Resource<ProductLicense>;

export interface ProductLicenses {
  activeProductLicenses: Array<ProductLicenseResource>;
  inactiveProductLicenses: Array<ProductLicenseResource>;
}
export type ProductLicensesResource = Resource<ProductLicenses>;

export type RecommendationEngineResource = Resource<RecommendationEngine>;

export type ShareUrlResource = Resource<ShareUrl>;

export type LearningCollectionBase = {
  id: number;
  name: string;
  internalName: StructuredMaybe<string>;
  publishedAt: StructuredMaybe<Date>;
  shareUrl: StructuredMaybe<ShareUrlResource>;
  competencies: AttributeResource[];
};

export interface LearningCollectionSummary extends LearningCollectionBase {
  experiencesCount: number;
  lastRegistrationDate: StructuredMaybe<Date>;
  studentMembersCount: number;
}

export type LearningCollectionSummaryResource = Resource<LearningCollectionSummary>;

export interface LearningCollection extends LearningCollectionBase {
  description: string;
  productCollections: ProductCollectionResource[];
  programFamilyShareUrls: PublicNameProgramFamilyShareUrl[];
  possibleShareUrlCount: number;
  duplicateProgramDeepLinkCount: number;
  totalShareUrlCount: number;
  filteredShareUrlCount: number;
}

export type LearningCollectionResource = Resource<LearningCollection>;

export interface LearningCollectionsMetadata extends PaginationMetadata {
  totalUnfilteredCount: number;
}

export type LearningCollectionSummariesResource = ResourceWithMetadata<
  LearningCollectionSummaryResource[],
  LearningCollectionsMetadata
>;

interface SharedState {
  searchParameters: SearchParameters;
  sortParameters: SearchParameters;
  page: number;
  selectedOrganization: Maybe<StringOption>;
  data: Maybe<LearningCollectionSummariesResource>;
}
export interface Waiting extends SharedState {
  kind: 'waiting';
}

export interface Ready extends SharedState {
  kind: 'ready';
}

export interface Updating extends SharedState {
  kind: 'updating';
}

export type LearningCollectionsState = Ready | Updating | Waiting;

export const ready = (
  state: LearningCollectionsState,
  data: Maybe<LearningCollectionSummariesResource>,
): Ready => ({
  ...state,
  kind: 'ready',
  data,
});

const updatedSearchParameters = (
  searchParameters: SearchParameters,
  searchParameter: SearchParameter,
) => searchParameters.filter((sp) => sp.key !== searchParameter.key).concat([searchParameter]);

export const updatingOrganizationParameters = (state: Ready, option: StringOption): Updating => {
  return {
    ...state,
    kind: 'updating',
    page: 1,
    searchParameters: updatedSearchParameters(state.searchParameters, {
      key: 'organization_id',
      value: option.value,
    }),
    selectedOrganization: just(option),
  };
};

export const waiting = (): Waiting => ({
  kind: 'waiting',
  sortParameters: [
    {
      value: 'updated_at-desc',
      key: 'sort',
    },
  ],
  page: 1,
  selectedOrganization: currentUserStore.organizationOption,
  data: nothing(),
  ...defaultOrgFilter(),
});

export const resetSearchParameters = (state: Ready): Updating => ({
  kind: 'updating',
  selectedOrganization: currentUserStore.organizationOption,
  sortParameters: state.sortParameters,
  page: 1,
  data: nothing(),
  ...defaultOrgFilter(),
});

export const updatingSearchParameters = (
  searchParameter: SearchParameter,
  state: Ready,
): Updating => {
  return {
    ...state,
    kind: 'updating',
    page: 1,
    searchParameters: updatedSearchParameters(state.searchParameters, searchParameter),
  };
};

export const updatingSortParameters = (sortParameter: SearchParameter, state: Ready): Updating => ({
  ...state,
  kind: 'updating',
  page: 1,
  sortParameters: [sortParameter],
});

export const updateLearningCollectionsList = (state: Ready): Updating => ({
  ...state,
  kind: 'updating',
});

export const changePage = (state: Ready, page: number): Updating => ({
  ...state,
  kind: 'updating',
  page,
});
